import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowLeft, Keyboard, Lock, RefreshCw, Send, Undo2 } from 'lucide-react';
import { Identity, StudyDetail } from '../types';
import { acquireLock, heartbeatLock, releaseLock } from '../api';
import { loadSignalWindow, SignalWindow } from '../signal';
import {
  BeatMarker,
  Overview,
  RECLASSIFY_CLASSES,
  ReviewDelta,
  ReviewWorkspace as Workspace,
  TriageItem,
  acknowledgeCritical,
  applyDelta,
  applyDeltaToOverview,
  bulkAcknowledgeCritical,
  bulkConfirm,
  fetchBeatMarkers,
  fetchOverview,
  fetchProgress,
  fetchWorkspace,
  formatStudyTime,
  relabelCluster,
  requestReanalysis,
  returnToReview,
  selectStrip,
  submitForSignature,
  undo,
  verdict,
} from '../review';
import { BEAT_MARKER_MAX_SPAN_SECONDS, WaveformViewer, wallClock } from './WaveformViewer';
import { AnalysisProgress } from './AnalysisProgress';
import { OverviewLanes } from './OverviewLanes';
import { TriageQueue } from './TriageQueue';
import { StatsRail } from './StatsRail';
import { ClusterGallery } from './ClusterGallery';

const STRIP_SECONDS = 10;
const CONTEXT_SECONDS = 600;
const HEARTBEAT_MS = 60_000;

interface Props {
  studyId: string;
  study: StudyDetail;
  identity: Identity;
  onBack: () => void;
}

export const ReviewWorkspaceView: React.FC<Props> = ({ studyId, study, identity, onBack }) => {
  // `sample_rate_hz`/`duration_seconds` are only ever unset while a study is INGESTING or
  // INGEST_FAILED (§13.6 I1/I2) — App.tsx renders a distinct panel for both of those and never
  // mounts this component for them, so both are always present by the time this runs. Asserted
  // rather than left optional throughout, which would turn every one of this screen's many uses
  // of the sample rate into a null check for a state that, here, cannot occur.
  const fs = study.sample_rate_hz!;
  const totalSamples = Math.round(study.duration_seconds! * fs);
  const isPhysician = identity.roles.includes('PHYSICIAN');

  const [ws, setWs] = useState<Workspace | null>(null);
  const [overview, setOverview] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Transient confirmation for an action whose effect is not immediately visible on screen. */
  const [notice, setNotice] = useState<string | null>(null);
  const [progress, setProgress] = useState<Awaited<ReturnType<typeof fetchProgress>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [lockProblem, setLockProblem] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [startSample, setStartSample] = useState(0);
  const [spanSamples, setSpanSamples] = useState(Math.min(totalSamples || 1, Math.round(fs * STRIP_SECONDS)));
  const [waveforms, setWaveforms] = useState<SignalWindow[]>([]);
  const [waveLoading, setWaveLoading] = useState(false);
  const [markers, setMarkers] = useState<BeatMarker[]>([]);
  const [bulkPending, setBulkPending] = useState<{ cls: string; count: number } | null>(null);
  const [showKeys, setShowKeys] = useState(false);
  const contextRestore = useRef<{ start: number; span: number } | null>(null);
  // The class currently being stepped through one event at a time (§ event-by-event review) —
  // separate from `selectedId`, since selecting an item and "stepping through a class" are
  // different intents: clicking one row in the flat queue shouldn't silently put you in a
  // stepping mode you didn't ask for, but once you *have* asked for one, j/k and Prev/Next should
  // stay scoped to it instead of jumping across classes by priority.
  const [activeClass, setActiveClass] = useState<string | null>(null);

  // ── Lock: taken on the first edit, renewed on a timer, released on close ───────────────────
  //
  // **Not on open** (DESIGN.md §13.6 R6). Opening a study is reading it, and the lock exists to
  // stop two reviewers *editing* it at once (§7.2). Locking on open meant a physician glancing at
  // a study to answer a question held it for five minutes, so a colleague who actually wanted to
  // work on it was refused — and every glance flipped the study to IN_REVIEW, which is a claim
  // about the workflow that nobody made. `ensureLock` is called by `act()` before the first
  // mutation instead, which is the moment the exclusion is actually needed.
  //
  // The heartbeat no longer swallows its failures. It did, so a lock lost to expiry or to an
  // admin left the header cheerfully saying "Locked by you" while every subsequent action 409ed —
  // the reviewer's screen and the server disagreed about who held the study, and the screen was
  // the confident one.
  const holdsLock = useRef(false);
  useEffect(() => {
    holdsLock.current = false;
    const timer = setInterval(() => {
      if (!holdsLock.current) return;
      void heartbeatLock(studyId).catch((err: Error) => {
        holdsLock.current = false;
        setLockProblem(
          err.message || 'The review lock was lost. Reacquire it before making further changes.',
        );
      });
    }, HEARTBEAT_MS);
    return () => {
      clearInterval(timer);
      if (holdsLock.current) void releaseLock(studyId).catch(() => undefined);
      holdsLock.current = false;
    };
  }, [studyId]);

  const ensureLock = useCallback(async () => {
    if (holdsLock.current) return;
    await acquireLock(studyId);
    holdsLock.current = true;
    setLockProblem(null);
  }, [studyId]);

  const load = useCallback(async () => {
    try {
      const next = await fetchWorkspace(studyId);
      setWs(next);
      if (next.analysis) setOverview(await fetchOverview(studyId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load the review workspace.');
    }
  }, [studyId]);
  useEffect(() => void load(), [load]);

  // Poll while there is no completed run: a study can sit at AWAITING_ANALYSIS or ANALYZING for
  // anywhere from seconds to (rarely, on a very large recording) minutes, and the previous portal
  // gave no indication anything was happening — indistinguishable from stuck.
  useEffect(() => {
    // Polls while there is no completed run **or** while a newer run is in flight (2026-09-21).
    // The original condition was `if (ws.analysis) return` — so the moment a study had findings,
    // polling stopped forever, and a re-analysis on an already-analysed study produced no
    // progress, no ETA and no sign anything was happening. The request now moves the study to
    // AWAITING_ANALYSIS, which is what makes the second half of this condition true.
    const reanalysing = !!ws && ws.status !== 'IN_REVIEW' && ws.status !== 'READY_FOR_REVIEW'
      && ws.status !== 'PENDING_SIGNATURE' && ws.status !== 'SIGNED' && ws.status !== 'AMENDED';
    if (ws?.analysis && !reanalysing) return;
    let alive = true;
    const tick = async () => {
      try {
        const p = await fetchProgress(studyId);
        if (!alive) return;
        setProgress(p);
        if (p.study_status === 'READY_FOR_REVIEW' || p.run_status === 'COMPLETED') void load();
      } catch {
        /* transient; the next tick retries */
      }
    };
    void tick();
    const timer = setInterval(tick, 4000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [studyId, ws?.analysis, load]);

  const editable = !!ws && !lockProblem && (ws.status === 'READY_FOR_REVIEW' || ws.status === 'IN_REVIEW');
  const queue = useMemo(() => ws?.queue ?? [], [ws]);
  const selected = queue.find((i) => i.id === selectedId) ?? null;

  const focus = useCallback(
    (item: TriageItem) => {
      setSelectedId(item.id);
      contextRestore.current = null;
      const start = item.strip_start_seconds ?? Math.max(0, item.onset_seconds - 3);
      const end = item.strip_end_seconds ?? start + STRIP_SECONDS;
      const span = Math.round(Math.max(STRIP_SECONDS, end - start) * fs);
      setSpanSamples(Math.min(totalSamples, span));
      setStartSample(Math.max(0, Math.min(Math.round(start * fs), totalSamples - span)));
    },
    [fs, totalSamples],
  );

  useEffect(() => {
    if (!selectedId && queue.length) focus(queue[0]);
  }, [queue, selectedId, focus]);

  // ── Signal and beat markers for the current window ─────────────────────────────────────────
  //
  // A drag or the position slider can fire dozens of window changes a second. Earlier this only
  // ignored a stale response (`cancelled`, checked before applying it) rather than cancelling the
  // request itself — the request kept running, and browsers cap concurrent connections per origin
  // (commonly 6), so a fast drag could queue far more range fetches than that. The *correct* final
  // window's request then sat behind a backlog of abandoned ones, and the waveform stayed on an old
  // frame until that backlog drained — "the grid moves, the trace doesn't" (found 2026-09-14, from
  // real use). An AbortController actually frees the connection instead of just discarding the result.
  useEffect(() => {
    if (!study.channels.length) return;
    const controller = new AbortController();
    setWaveLoading(true);
    // Every channel at once, so the leads are read together on one time axis — artifact in one lead
    // and not the other is exactly what the reviewer needs to see side by side (§6.6).
    const px = Math.max(600, Math.round(window.innerWidth * 0.6));
    Promise.all(
      study.channels.map((c) => loadSignalWindow(studyId, c.label, startSample, spanSamples, px, controller.signal)),
    )
      .then((next) => setWaveforms(next))
      .catch((err) => {
        if (err?.name !== 'AbortError') setWaveforms([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setWaveLoading(false);
      });
    const startS = startSample / fs;
    const endS = (startSample + spanSamples) / fs;
    if (ws?.analysis && endS - startS <= BEAT_MARKER_MAX_SPAN_SECONDS) {
      fetchBeatMarkers(studyId, startS, endS, controller.signal)
        .then((m) => setMarkers(m))
        .catch(() => undefined);
    } else {
      setMarkers([]);
    }
    return () => controller.abort();
  }, [studyId, study.channels, startSample, spanSamples, fs, ws?.analysis]);

  // ── Actions ────────────────────────────────────────────────────────────────────────────────
  // Read by act() so it merges into the workspace as it is now, not as it was when act was created.
  const wsRef = useRef<Workspace | null>(null);
  wsRef.current = ws;

  const act = useCallback(
    async (fn: () => Promise<ReviewDelta>, advance = false) => {
      if (busy) return;
      setBusy(true);
      try {
        // Taken here, on the first edit, rather than on open (§13.6 R6): reading a study is not
        // editing it, and holding it for five minutes because someone glanced at it locks out the
        // colleague who actually wanted to work.
        await ensureLock();
        const delta = await fn();
        const current = wsRef.current;
        setError(null);
        if (!current || delta.run_id !== (current.analysis?.id ?? null)) {
          // A different run landed underneath this screen (re-analysis): its findings are not the
          // ones held here, so reload rather than merge into the wrong run.
          await load();
          return;
        }
        // Merge what changed instead of replacing everything — actions used to return the whole
        // workspace (~4 MB per keystroke at 8,296 findings) and refetch the overview on top.
        const next = applyDelta(current, delta);
        setWs(next);
        setOverview((o) => (o ? applyDeltaToOverview(o, delta) : o));
        // The overview's HR trend is computed from beat labels, which only a cluster relabel moves.
        if (delta.clusters.length > 0) fetchOverview(studyId).then(setOverview).catch(() => undefined);
        if (advance && selectedId) {
          const order = next.queue;
          const at = order.findIndex((i) => i.id === selectedId);
          const after = order.slice(at + 1).concat(order.slice(0, at)).find((i) => i.status === 'UNREVIEWED');
          if (after) focus(after);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'That action was refused.');
      } finally {
        setBusy(false);
      }
    },
    [busy, ensureLock, focus, load, selectedId, studyId],
  );

  /**
   * Request a fresh analysis run.
   *
   * Not routed through `act()`: that merges a `ReviewDelta` into the workspace, and this returns
   * no delta because nothing has changed yet — it queues a job. The findings on screen stay the
   * current run's until the worker finishes and a newer completed run exists, which is the honest
   * thing to show. Blanking the queue would claim the study had no findings when it has these.
   */
  const reanalyse = useCallback(
    async (reason: string) => {
      if (busy) return;
      setBusy(true);
      try {
        await ensureLock();
        const queued = await requestReanalysis(studyId, reason);
        setError(null);
        setNotice(queued.message);
        await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Re-analysis was refused.');
      } finally {
        setBusy(false);
      }
    },
    [busy, ensureLock, load, studyId],
  );

  const move = (delta: number) => {
    if (!queue.length) return;
    const at = Math.max(0, queue.findIndex((i) => i.id === selectedId));
    focus(queue[Math.min(queue.length - 1, Math.max(0, at + delta))]);
  };

  // Chronological, not priority order: "walk every pause" is about seeing them in the order the
  // patient had them, unlike the flat queue (§ triage ordering), which is sorted by urgency.
  const classItems = useMemo(
    () => (activeClass ? queue.filter((i) => i.rhythm_class === activeClass).sort((a, b) => a.onset_seconds - b.onset_seconds) : []),
    [queue, activeClass],
  );

  const activateClass = (cls: string) => {
    setActiveClass(cls);
    const items = queue.filter((i) => i.rhythm_class === cls).sort((a, b) => a.onset_seconds - b.onset_seconds);
    focus(items.find((i) => i.status === 'UNREVIEWED') ?? items[0]);
  };

  // Prefers the next still-open item in that direction — matches the post-verdict auto-advance
  // in `act()` above, so confirming/rejecting and manually stepping land you in the same place.
  // Falls back to any item once the open ones in that direction run out, rather than getting stuck.
  const stepClass = (delta: number) => {
    if (!classItems.length) return;
    const at = Math.max(0, classItems.findIndex((i) => i.id === selectedId));
    let i = at + delta;
    while (i >= 0 && i < classItems.length && classItems[i].status !== 'UNREVIEWED') i += delta;
    if (i < 0 || i >= classItems.length) i = Math.min(classItems.length - 1, Math.max(0, at + delta));
    focus(classItems[i]);
  };

  const zoom = (factor: number) => {
    const centre = startSample + spanSamples / 2;
    const span = Math.max(Math.round(fs * 2), Math.min(totalSamples, Math.round(spanSamples * factor)));
    setSpanSamples(span);
    setStartSample(Math.max(0, Math.round(centre - span / 2)));
  };

  const toggleContext = () => {
    if (contextRestore.current) {
      setStartSample(contextRestore.current.start);
      setSpanSamples(contextRestore.current.span);
      contextRestore.current = null;
      return;
    }
    contextRestore.current = { start: startSample, span: spanSamples };
    const centre = startSample + spanSamples / 2;
    const span = Math.min(totalSamples, Math.round(CONTEXT_SECONDS * fs));
    setSpanSamples(span);
    setStartSample(Math.max(0, Math.min(Math.round(centre - span / 2), totalSamples - span)));
  };

  // ── Keyboard model (§7.4): single hand, no chords in the hot loop ──────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (['INPUT', 'SELECT', 'TEXTAREA'].includes(target.tagName) || e.metaKey || e.ctrlKey || e.altKey) return;
      const key = e.key;
      if (key === 'ArrowRight' || key === 'ArrowLeft') {
        e.preventDefault();
        const step = (e.shiftKey ? 1 : 0.5) * spanSamples * (key === 'ArrowRight' ? 1 : -1);
        contextRestore.current = null;
        setStartSample(Math.max(0, Math.min(Math.round(startSample + step), totalSamples - spanSamples)));
      } else if (key === 'Home') setStartSample(0);
      else if (key === 'End') setStartSample(Math.max(0, totalSamples - spanSamples));
      else if (key === 'j') activeClass ? stepClass(1) : move(1);
      else if (key === 'k') activeClass ? stepClass(-1) : move(-1);
      else if (key === 'z') zoom(2);
      else if (key === 'Z') zoom(0.5);
      else if (key === ' ') {
        e.preventDefault();
        toggleContext();
      } else if (key === '?') setShowKeys((v) => !v);
      else if (!editable) return;
      else if (key === 'u') void act(() => undo(studyId));
      else if (!selected) return;
      else if (key === 'a') void act(() => verdict(studyId, selected.id, 'confirm'), true);
      else if (key === 'x') void act(() => verdict(studyId, selected.id, 'reject'), true);
      else if (key === 's') void act(() => selectStrip(studyId, selected.id, !selected.selected_for_report));
      else if (key === 'c' && selected.is_critical) void act(() => acknowledgeCritical(studyId, selected.id));
      else if (/^[1-9]$/.test(key)) {
        const cls = RECLASSIFY_CLASSES[Number(key) - 1];
        void act(() => verdict(studyId, selected.id, 'reclassify', cls), true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const openCriticals = useMemo(
    () => queue.filter((i) => i.is_critical && !i.critical_acknowledged_at && i.status !== 'REJECTED'),
    [queue],
  );
  const criticalGroups = useMemo(() => {
    const byClass = new Map<string, TriageItem[]>();
    for (const item of openCriticals) {
      byClass.set(item.rhythm_class, [...(byClass.get(item.rhythm_class) ?? []), item]);
    }
    return Array.from(byClass, ([rhythmClass, items]) => ({ rhythmClass, items }));
  }, [openCriticals]);

  const clock = useMemo(() => ({ wearStartIso: study.wear_start!, timeZone: study.study_timezone }), [study.wear_start, study.study_timezone]);
  // Stable references for the waveform: its draw effect depends on these, and rebuilding them inline
  // on every render meant any unrelated state change (a signal fetch landing, `busy` toggling) forced a
  // full canvas redraw that looped over every finding in the study (2026-09-15).
  const channelLabels = useMemo(() => study.channels.map((c) => c.label), [study.channels]);
  const viewerEpisodes = useMemo(() => queue.filter((i) => i.status !== 'REJECTED'), [queue]);
  const viewStart = startSample / fs;
  const viewEnd = (startSample + spanSamples) / fs;

  return (
    <div className="mx-auto max-w-[1600px] space-y-4 px-4 py-4">
      {/* Header */}
      <div className="glass-panel flex flex-wrap items-center justify-between gap-3 rounded-2xl p-3">
        <div className="flex items-center gap-2.5">
          <button onClick={onBack} className="rounded-lg border border-slate-800 bg-slate-900 p-1.5 text-slate-300 hover:text-white">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-bold text-white">{study.patient_name}</h2>
              <span className="mono rounded border border-slate-700 px-1.5 py-0.5 text-xs text-slate-300">{study.patient_ref}</span>
              <span className="mono rounded border border-cyan-800 bg-cyan-950 px-1.5 py-0.5 text-xs text-cyan-300">{study.reference}</span>
              {study.tenant_name && <span className="text-xs text-slate-400">{study.tenant_name}</span>}
              {/* Demographics only when recorded — never a default. */}
              {(study.patient_gender || study.patient_age) && (
                <span className="text-xs text-slate-400">{[study.patient_gender, study.patient_age && `${study.patient_age} y`].filter(Boolean).join(', ')}</span>
              )}
              <span className="text-xs text-slate-500">
                · {study.device_model} · {study.num_channels} ch @ {fs.toFixed(2)} Hz · {(study.duration_seconds! / 3600).toFixed(1)} h · {ws?.status ?? study.status}
              </span>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {lockProblem ? (
            <span className="flex items-center gap-1.5 rounded-lg border border-amber-800 bg-amber-950 px-3 py-1.5 text-amber-300">
              <Lock className="h-3.5 w-3.5" /> Read-only: {lockProblem}
            </span>
          ) : (
            <span className="flex items-center gap-1.5 rounded-lg border border-emerald-800 bg-emerald-950 px-3 py-1.5 text-emerald-300">
              <Lock className="h-3.5 w-3.5" /> Locked by you
            </span>
          )}
          <button onClick={() => setShowKeys((v) => !v)} className="flex items-center gap-1 rounded-lg border border-slate-700 px-2.5 py-1.5 text-slate-300">
            <Keyboard className="h-3.5 w-3.5" /> Keys
          </button>
          <button disabled={!editable || busy} onClick={() => void act(() => undo(studyId))}
            className="flex items-center gap-1 rounded-lg border border-slate-700 px-2.5 py-1.5 text-slate-300 disabled:opacity-40">
            <Undo2 className="h-3.5 w-3.5" /> Undo
          </button>
          {/* Re-analysis (D54/D55). The endpoint, the episode-to-label matching and the proposal
              rendering all shipped before this button did, so the feature existed and was
              unreachable — found 2026-09-21 by someone looking for it in the UI.

              Deliberately not styled as a primary action: it discards this run's findings and
              turns every verdict against them into a proposal to be re-confirmed. The confirm
              step spells that out rather than relying on the reason prompt to imply it. */}
          <button
            disabled={busy || !!lockProblem || !ws?.analysis}
            title={
              ws?.analysis
                ? 'Run the analysis again. Your verdicts are kept and offered back as proposals.'
                : 'This study has not been analysed yet.'
            }
            onClick={() => {
              const reason = window.prompt(
                'Re-analyse this study?\n\n' +
                'The current findings are replaced by a fresh run. Your verdicts are NOT lost — ' +
                'each one is offered back against the new findings as a proposal for you to ' +
                'confirm, because a newer engine may legitimately disagree with a correction ' +
                'made against an older one.\n\n' +
                'Why are you re-analysing? (recorded in the audit trail)',
              );
              if (reason && reason.trim()) void reanalyse(reason.trim());
            }}
            className="flex items-center gap-1 rounded-lg border border-slate-700 px-2.5 py-1.5 text-slate-300 disabled:opacity-40"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Re-analyse
          </button>
          {ws?.status === 'PENDING_SIGNATURE' ? (
            isPhysician && (
              <button
                disabled={busy || !!lockProblem}
                onClick={() => {
                  const reason = window.prompt('Why is this study going back to review?');
                  if (reason) void act(() => returnToReview(studyId, reason));
                }}
                className="rounded-lg border border-amber-700 bg-amber-900 px-3 py-1.5 font-semibold text-white disabled:opacity-40"
              >
                Return to review
              </button>
            )
          ) : (
            <button
              disabled={!editable || busy || (ws?.signature_blockers.length ?? 1) > 0}
              title={ws?.signature_blockers.join('\n')}
              onClick={() => void act(() => submitForSignature(studyId))}
              className="flex items-center gap-1 rounded-lg border border-cyan-700 bg-cyan-800 px-3 py-1.5 font-semibold text-white disabled:opacity-40"
            >
              <Send className="h-3.5 w-3.5" /> Submit for signature
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-center justify-between rounded-xl border border-rose-800 bg-rose-950 px-4 py-2 text-xs text-rose-200">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-rose-300">Dismiss</button>
        </div>
      )}

      {/* Re-analysis queues a job, so nothing on screen changes when it succeeds. Without this
          the button would look broken — the findings below are still the previous run's, and
          correctly so, until the new one completes. */}
      {notice && (
        <div className="flex items-center justify-between rounded-xl border border-cyan-800 bg-cyan-950 px-4 py-2 text-xs text-cyan-200">
          <span>{notice}</span>
          <button onClick={() => setNotice(null)} className="text-cyan-300">Dismiss</button>
        </div>
      )}

      {openCriticals.length > 0 && (
        <div className="space-y-1.5 rounded-xl border border-rose-700 bg-rose-950/80 p-2.5 text-xs text-rose-100">
          <p className="flex items-center gap-2 font-bold">
            <AlertTriangle className="h-4 w-4 shrink-0" /> {openCriticals.length} critical finding(s) awaiting acknowledgement
            <span
              className="cursor-help text-[10px] font-normal text-rose-400 underline decoration-dotted"
              title="Acknowledgement is recorded with your name and time and is not the same as accepting a finding as real — it means a human has seen the alarm fire, which is why it can be done for a whole class at once. The escalation protocol — who is called and how fast — is not yet defined (DESIGN.md §15 Q1), so this does not notify anyone."
            >
              what does acknowledging mean?
            </span>
          </p>
          {/* Grouped, not one chip per finding: a classifier with a known false-alarm rate (§11.7) can
             flag one class hundreds of times over a multi-day study, and a wall of individual chips is
             exactly what made this banner unusable (found 2026-09-14, from real use). */}
          <div className="flex flex-wrap gap-2">
            {criticalGroups.map((g) => (
              <span key={g.rhythmClass} className="flex items-center gap-2 rounded-lg border border-rose-800 bg-rose-900/60 px-2 py-1">
                {g.items.length <= 5 ? (
                  g.items.map((c) => (
                    <button key={c.id} className="font-semibold underline" onClick={() => focus(c)}>
                      {c.rhythm_class.toUpperCase()} {formatStudyTime(c.onset_seconds)}
                    </button>
                  ))
                ) : (
                  <button className="font-semibold underline" onClick={() => focus(g.items[0])}>
                    {g.items.length}× {g.rhythmClass.toUpperCase()}
                  </button>
                )}
                <button
                  disabled={(!editable && ws?.status !== 'PENDING_SIGNATURE') || !!lockProblem || busy}
                  onClick={() => void act(() => bulkAcknowledgeCritical(studyId, g.rhythmClass, g.items.length))}
                  className="rounded border border-rose-500 px-1.5 disabled:opacity-40"
                >
                  Acknowledge {g.items.length > 1 ? `all ${g.items.length}` : ''}
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {showKeys && (
        <div className="glass-panel mono grid grid-cols-2 gap-x-6 gap-y-1 rounded-xl p-4 text-xs text-slate-300 md:grid-cols-4">
          <span>← / → — pan half a window</span><span>Shift+← / → — pan a window</span><span>Home / End — study start / end</span>
          <span>j / k — next / previous (scoped to a class once you're stepping through one)</span><span>a — confirm</span><span>x — reject</span><span>u — undo</span>
          <span>s — toggle report strip</span><span>c — acknowledge critical</span><span>z / Z — zoom out / in</span><span>space — ±5 min context</span>
          {RECLASSIFY_CLASSES.map((cls, i) => <span key={cls}>{i + 1} — reclassify {cls}</span>)}
        </div>
      )}

      {/* Shown whenever a run is in flight, including a re-analysis of a study that already has
          findings — those stay readable below while the new run works. */}
      {progress && (!ws?.analysis || (progress.run_status && progress.run_status !== 'COMPLETED')
        || ws.status === 'AWAITING_ANALYSIS' || ws.status === 'ANALYZING') && (
        <AnalysisProgress
          progress={progress}
          busy={busy}
          onReanalyse={() => {
            const reason = window.prompt(
              'Run the analysis again?\n\nWhy? (recorded in the audit trail)',
              'Retrying a failed analysis',
            );
            if (reason && reason.trim()) void reanalyse(reason.trim());
          }}
        />
      )}

      {/* Triage list (left) is full height, matching the stacked overview+chart column beside it
          (§ layout, 2026-09-14) — the list is a one-time entry point, not something you keep
          scrolling back to once you're stepping through a class. Stats stay a persistent third
          column: the "how much is left" summary is worth having visible through the whole session,
          and there's nowhere else on the page it already belongs. */}
      {/* Breakpoint deliberately lower than Tailwind's default `xl` (1280px): at 1280 this stacked
          for any window narrower than that even on a large monitor (non-maximized, higher OS
          scaling) with no gradual response — found 2026-09-14 from real use ("bigger screens
          stacking"). Narrower fixed columns below buy back enough room to hold three-across much
          closer to `lg` (1024px) instead. */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[13rem_1fr_15rem] lg:items-stretch">
        <div className="flex h-full flex-col gap-3">
          <TriageQueue
            items={queue}
            selectedId={selectedId}
            editable={editable}
            activeClass={activeClass}
            onSelect={(id) => {
              const it = queue.find((q) => q.id === id);
              if (!it) return;
              if (it.rhythm_class !== activeClass) setActiveClass(null);
              focus(it);
            }}
            onActivateClass={activateClass}
            onBulkConfirm={(cls, count) => setBulkPending({ cls, count })}
            onBulkAcknowledge={(cls, count) => void act(() => bulkAcknowledgeCritical(studyId, cls, count))}
          />
          {bulkPending && (
            <div className="glass-panel rounded-2xl border-amber-800 bg-amber-950/60 p-3 text-xs text-amber-200">
              Confirm <strong>{bulkPending.count}</strong> unreviewed <strong>{bulkPending.cls}</strong> finding(s)
              without viewing each? Undo restores them one at a time if that turns out wrong.
              <div className="mt-2 flex gap-2">
                <button className="rounded border border-slate-600 px-2 py-0.5" onClick={() => setBulkPending(null)}>Cancel</button>
                <button
                  disabled={busy}
                  className="rounded border border-amber-600 bg-amber-800 px-2 py-0.5 font-semibold text-white disabled:opacity-40"
                  onClick={() => { const p = bulkPending; setBulkPending(null); void act(() => bulkConfirm(studyId, p.cls, p.count)); }}
                >
                  Confirm {bulkPending.count}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-3">
          {selected && (
            <div className="glass-panel flex flex-wrap items-center justify-between gap-2 rounded-xl px-4 py-2 text-xs">
              <span className="text-slate-300">
                <strong className="uppercase text-white">{selected.rhythm_class}</strong> · {formatStudyTime(selected.onset_seconds)} ·{' '}
                {selected.duration_seconds.toFixed(1)} s · {(selected.confidence * 100).toFixed(0)}% engine confidence
              </span>
              <span className="flex items-center gap-1.5">
                {activeClass && (
                  <span className="flex items-center gap-1 rounded-lg border border-cyan-800 bg-cyan-950/60 px-1 py-1 text-cyan-200">
                    <button
                      title={`Previous open ${activeClass} (k)`}
                      onClick={() => stepClass(-1)}
                      className="rounded px-1.5 py-0.5 hover:bg-cyan-900 disabled:opacity-40"
                      disabled={classItems.findIndex((i) => i.id === selectedId) <= 0}
                    >
                      ◀
                    </button>
                    <span className="mono px-1">
                      {activeClass} {Math.max(0, classItems.findIndex((i) => i.id === selectedId)) + 1}/{classItems.length}
                    </span>
                    <button
                      title={`Next open ${activeClass} (j)`}
                      onClick={() => stepClass(1)}
                      className="rounded px-1.5 py-0.5 hover:bg-cyan-900 disabled:opacity-40"
                      disabled={classItems.findIndex((i) => i.id === selectedId) >= classItems.length - 1}
                    >
                      ▶
                    </button>
                  </span>
                )}
                <button disabled={!editable || busy} onClick={() => void act(() => verdict(studyId, selected.id, 'confirm'), true)}
                  className="rounded border border-emerald-700 bg-emerald-900 px-2 py-1 text-emerald-100 disabled:opacity-40">Confirm (a)</button>
                <button disabled={!editable || busy} onClick={() => void act(() => verdict(studyId, selected.id, 'reject'), true)}
                  className="rounded border border-rose-700 bg-rose-900 px-2 py-1 text-rose-100 disabled:opacity-40">Reject (x)</button>
                <button disabled={!editable || busy || selected.status === 'REJECTED'} onClick={() => void act(() => selectStrip(studyId, selected.id, !selected.selected_for_report))}
                  className="rounded border border-cyan-700 px-2 py-1 text-cyan-200 disabled:opacity-40">
                  {selected.selected_for_report ? 'Remove strip (s)' : 'Use strip (s)'}
                </button>
              </span>
            </div>
          )}
          {/* Overview and chart merged into one panel — same width, no gap, one toolbar between
              them — so it's visible they're two zoom levels of the same timeline, always in sync,
              rather than two sections you have to remember relate to each other (§ layout,
              2026-09-14). Overview's own click-to-jump plus dragging the chart itself cover
              positioning between them, so the chart no longer needs its own separate slider
              (removed from WaveformViewer) — one way to scrub, not two. */}
          <div className="glass-panel space-y-3 rounded-2xl p-4">
            {overview && (
              <>
                <OverviewLanes overview={overview} viewStart={viewStart} viewEnd={viewEnd}
                  onSeek={(s) => {
                    contextRestore.current = null;
                    setActiveClass(null);
                    setStartSample(Math.max(0, Math.min(Math.round((s - spanSamples / fs / 2) * fs), totalSamples - spanSamples)));
                  }} />
                <div className="border-t border-slate-800" />
              </>
            )}
            <WaveformViewer
              waveforms={waveforms}
              channelLabels={channelLabels}
              clock={clock}
              startSample={startSample}
              spanSamples={spanSamples}
              totalSamples={totalSamples}
              sampleRateHz={fs}
              onWindowChange={(s, span) => {
                contextRestore.current = null;
                setSpanSamples(span);
                setStartSample(Math.max(0, Math.min(s, totalSamples - span)));
              }}
              episodes={viewerEpisodes}
              focusEpisodeId={selectedId}
              beatMarkers={markers}
              isLoading={waveLoading}
            />
          </div>
        </div>

        <div className="space-y-3">
          {ws?.statistics && ws.analysis && <StatsRail stats={ws.statistics} engineVersion={ws.analysis.engine_version} />}
          {ws && ws.signature_blockers.length > 0 && ws.analysis && (
            <div className="glass-panel rounded-2xl p-3 text-xs text-slate-400">
              <p className="mb-1 font-semibold text-slate-300">Before signature</p>
              <ul className="list-disc space-y-0.5 pl-4">{ws.signature_blockers.map((b) => <li key={b}>{b}</li>)}</ul>
            </div>
          )}
        </div>
      </div>

      {ws && ws.clusters.length > 0 && (
        <ClusterGallery clusters={ws.clusters} disabled={!editable || busy}
          onRelabel={(c, label) => void act(() => relabelCluster(studyId, c.id, label, c.member_count))} />
      )}
    </div>
  );
};
